// components/orders/BanquetsTab.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import UzPhoneField, { digitsUz9, isValidUz9 } from '../common/UzPhoneField';

const fmt = (n) => Number(n).toLocaleString('ru-RU');
const fmtDate = (d) => {
  if (!d) return '—';
  const s = typeof d === 'string' ? d.slice(0, 10) : d;
  return new Date(s + 'T00:00:00').toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric', weekday: 'short',
  });
};

const STATUS = {
  pending:   { label: 'Ожидает',     color: '#FF9500', bg: '#FFF3E0' },
  confirmed: { label: 'Подтверждён', color: '#34C759', bg: '#E8F5E9' },
  cancelled: { label: 'Отменён',     color: '#E85D4A', bg: '#FFEBEE' },
};

function toUzValue(raw) {
  if (!raw || !String(raw).trim()) return '+998';
  return '+998' + String(raw).replace(/\+998/g, '').replace(/\D/g, '').slice(0, 9);
}

const EMPTY_FORM = {
  customer_name: '', phone: '+998', date: '', time: '',
  guests_count: '', prepayment: '', discount: '', notes: '',
};

export default function BanquetsTab({ api, restaurantId, userRole, menuItems = [], categories = [] }) {
  const [banquets, setBanquets]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [message, setMessage]     = useState('');
  const [showForm, setShowForm]   = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [cart, setCart]           = useState({});   // { menuItemId: quantity }
  const [search, setSearch]       = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [saving, setSaving]           = useState(false);
  const [sendingTg, setSendingTg]     = useState(null); // banquet id
  const [statusFilter, setStatusFilter] = useState('all');
  const [phoneError, setPhoneError]  = useState('');

  const showMsg = (m) => { setMessage(m); setTimeout(() => setMessage(''), 3000); };

  const fetchBanquets = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/staff/banquets?restaurantId=${restaurantId}`);
      setBanquets(data.banquets || []);
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Ошибка загрузки'));
    } finally { setLoading(false); }
  }, [api, restaurantId]);

  useEffect(() => { fetchBanquets(); }, [fetchBanquets]);

  // ── Выбор блюд ───────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return menuItems.filter((i) => {
      const matchCat = catFilter === 'all' || i.category_id === catFilter;
      const matchQ   = (i.name_ru || i.name || '').toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [menuItems, search, catFilter]);

  const cartItems = menuItems.filter((i) => cart[i.id] > 0);

  const subtotal = cartItems.reduce((s, i) => s + Number(i.price) * cart[i.id], 0);
  const discountPct = Math.min(100, Math.max(0, Number(form.discount) || 0));
  const discountAmt = Math.round(subtotal * discountPct / 100);
  const total       = subtotal - discountAmt;
  const prepayment  = Number(form.prepayment) || 0;
  const balance     = total - prepayment;

  const addItem = (item) =>
    setCart((p) => ({ ...p, [item.id]: (p[item.id] || 0) + 1 }));
  const changeQty = (id, delta) =>
    setCart((p) => {
      const next = (p[id] || 0) + delta;
      if (next <= 0) { const { [id]: _, ...rest } = p; return rest; }
      return { ...p, [id]: next };
    });

  // ── Открыть форму ────────────────────────────────────────
  const openCreate = () => {
    setEditId(null);
    setPhoneError('');
    setForm(EMPTY_FORM);
    setCart({});
    setSearch(''); setCatFilter('all');
    setShowForm(true);
  };

  const openEdit = (b) => {
    setEditId(b.id);
    setPhoneError('');
    setForm({
      customer_name: b.customer_name || '',
      phone:         toUzValue(b.phone),
      date:          b.date?.slice(0, 10) || '',
      time:          b.time?.slice(0, 5)  || '',
      guests_count:  String(b.guests_count || ''),
      prepayment:    String(b.prepayment   || ''),
      discount:      String(b.discount     || ''),
      notes:         b.notes || '',
    });
    // Восстанавливаем корзину из сохранённых позиций
    const initCart = {};
    (b.items || []).forEach((it) => {
      if (it.menu_item_id) initCart[it.menu_item_id] = it.quantity;
    });
    setCart(initCart);
    setSearch(''); setCatFilter('all');
    setShowForm(true);
  };

  // ── Сохранить ────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.customer_name || !form.date || !form.time) {
      showMsg('⚠️ Заполните имя, дату и время'); return;
    }
    const phoneDigits = digitsUz9(form.phone);
    if (phoneDigits.length > 0 && !isValidUz9(form.phone)) {
      setPhoneError('Введите 9 цифр после +998 или оставьте пустым');
      showMsg('⚠️ Телефон: 9 цифр после +998');
      return;
    }
    setPhoneError('');
    setSaving(true);
    try {
      const items = cartItems.map((i) => ({
        menu_item_id: i.id,
        name:     i.name_ru || i.name,
        price:    Number(i.price),
        quantity: cart[i.id],
      }));
      const payload = {
        ...form,
        phone: isValidUz9(form.phone) ? `+998${phoneDigits}` : '',
        guests_count: Number(form.guests_count) || 1,
        prepayment:   Number(form.prepayment)   || 0,
        discount:     discountPct,
        items,
        restaurantId,
      };
      if (editId) {
        await api.patch(`/api/staff/banquets/${editId}`, payload);
        showMsg('✅ Банкет обновлён');
      } else {
        await api.post('/api/staff/banquets', payload);
        showMsg('✅ Банкет создан');
      }
      setShowForm(false);
      fetchBanquets();
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Ошибка'));
    } finally { setSaving(false); }
  };

  const handleStatus = async (id, status) => {
    try {
      await api.patch(`/api/staff/banquets/${id}`, { status, restaurantId });
      fetchBanquets();
    } catch { showMsg('❌ Ошибка'); }
  };

  const handleSendTelegram = async (id) => {
    setSendingTg(id);
    try {
      await api.post(`/api/staff/banquets/${id}/send-telegram`, { restaurantId });
      showMsg('✅ Отправлено в Telegram!');
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Ошибка отправки'));
    } finally {
      setSendingTg(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить банкет?')) return;
    try {
      await api.delete(`/api/staff/banquets/${id}?restaurantId=${restaurantId}`);
      showMsg('🗑️ Удалён'); fetchBanquets();
    } catch { showMsg('❌ Ошибка удаления'); }
  };

  const filtered2 = statusFilter === 'all'
    ? banquets
    : banquets.filter((b) => b.status === statusFilter);

  const upcoming = banquets.filter(
    (b) => b.status !== 'cancelled' && new Date(b.date) >= new Date(new Date().toDateString())
  ).length;

  return (
    <div>
      {message && (
        <div style={{
          padding: '12px 16px', borderRadius: '12px', marginBottom: '12px',
          backgroundColor: message.startsWith('❌') || message.startsWith('⚠️') ? '#FFEBEE' : '#E8F5E9',
          fontWeight: '500', textAlign: 'center', fontSize: '14px',
        }}>{message}</div>
      )}

      {/* Шапка */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: '14px' }}>
        <div>
          <div style={{ fontSize: '16px', fontWeight: '700' }}>🥂 Банкеты</div>
          {upcoming > 0 && (
            <div style={{ fontSize: '12px', color: '#34C759', fontWeight: '600' }}>
              {upcoming} предстоящих
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={fetchBanquets} style={ghostBtn}>🔄</button>
          <button onClick={openCreate} style={primaryBtn}>+ Добавить</button>
        </div>
      </div>

      {/* Фильтр */}
      <div style={{ display: 'flex', gap: '6px', overflowX: 'auto',
        paddingBottom: '8px', marginBottom: '12px', scrollbarWidth: 'none' }}>
        {[{ key: 'all', label: 'Все' }, ...Object.entries(STATUS).map(([k, v]) => ({ key: k, label: v.label }))].map((s) => (
          <button key={s.key} onClick={() => setStatusFilter(s.key)} style={{
            flexShrink: 0, padding: '5px 12px', borderRadius: '100px', border: 'none',
            backgroundColor: statusFilter === s.key ? '#000' : '#F0F0F0',
            color: statusFilter === s.key ? '#fff' : '#555',
            fontSize: '12px', fontWeight: '600', cursor: 'pointer',
          }}>{s.label}</button>
        ))}
      </div>

      {/* Список */}
      {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Загрузка...</div>}
      {!loading && filtered2.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999', fontSize: '14px' }}>Банкетов нет</div>
      )}

      {filtered2.map((b) => {
        const st = STATUS[b.status] || STATUS.pending;
        const bItems = b.items || [];
        const bSubtotal = bItems.reduce((s, i) => s + Number(i.price) * i.quantity, 0);
        const bDiscount = Math.round(bSubtotal * Number(b.discount || 0) / 100);
        const bTotal    = bSubtotal - bDiscount;
        return (
          <div key={b.id} style={{
            backgroundColor: '#fff', borderRadius: '16px', padding: '16px',
            marginBottom: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            opacity: b.status === 'cancelled' ? 0.6 : 1,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-start', marginBottom: '10px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '2px' }}>
                  {b.customer_name}
                </div>
                {b.phone && (
                  <a href={`tel:${b.phone}`} style={{ fontSize: '13px', color: '#007AFF', textDecoration: 'none' }}>
                    📞 {b.phone}
                  </a>
                )}
              </div>
              <span style={{
                padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
                backgroundColor: st.bg, color: st.color, flexShrink: 0, marginLeft: '8px',
              }}>{st.label}</span>
            </div>

            {/* Детали */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <InfoCell icon="📅" label="Дата" value={fmtDate(b.date)} />
              <InfoCell icon="🕐" label="Время" value={b.time?.slice(0, 5)} />
              <InfoCell icon="👥" label="Гостей" value={`${b.guests_count} чел.`} />
              <InfoCell icon="💰" label="Предоплата"
                value={Number(b.prepayment) > 0 ? fmt(b.prepayment) + ' сум' : '—'}
                valueColor={Number(b.prepayment) > 0 ? '#34C759' : '#999'} />
            </div>

            {/* Меню */}
            {bItems.length > 0 && (
              <div style={{
                backgroundColor: '#F8F8F8', borderRadius: '12px',
                padding: '10px 12px', marginBottom: '12px',
              }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#555', marginBottom: '6px' }}>
                  🍽️ Меню ({bItems.length} поз.)
                </div>
                {bItems.map((it, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                    fontSize: '13px', padding: '3px 0', borderBottom: '1px solid #EFEFEF' }}>
                    <span>{it.name} × {it.quantity}</span>
                    <span style={{ color: '#666' }}>{fmt(Number(it.price) * it.quantity)} сум</span>
                  </div>
                ))}
                <div style={{ marginTop: '8px', fontSize: '13px' }}>
                  {Number(b.discount) > 0 && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#999' }}>
                        <span>Подитог</span><span>{fmt(bSubtotal)} сум</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#FF9500' }}>
                        <span>Скидка {b.discount}%</span><span>−{fmt(bDiscount)} сум</span>
                      </div>
                    </>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    fontWeight: '700', fontSize: '14px', paddingTop: '4px' }}>
                    <span>Итого</span><span>{fmt(bTotal)} сум</span>
                  </div>
                  {Number(b.prepayment) > 0 && bTotal > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      color: '#E85D4A', fontSize: '13px', marginTop: '2px' }}>
                      <span>Остаток</span><span>{fmt(bTotal - Number(b.prepayment))} сум</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {b.notes && (
              <div style={{ fontSize: '13px', color: '#666', backgroundColor: '#F8F8F8',
                borderRadius: '10px', padding: '8px 10px', marginBottom: '12px' }}>
                📝 {b.notes}
              </div>
            )}

            {/* Кнопки */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {b.status === 'pending' && (
                <button onClick={() => handleStatus(b.id, 'confirmed')}
                  style={actionBtn('#34C759')}>✅ Подтвердить</button>
              )}
              {b.status === 'confirmed' && (
                <button onClick={() => handleStatus(b.id, 'pending')}
                  style={actionBtn('#FF9500')}>↩️ В ожидание</button>
              )}
              {b.status !== 'cancelled' && (
                <button onClick={() => handleStatus(b.id, 'cancelled')}
                  style={actionBtn('#E85D4A')}>✕ Отмена</button>
              )}
              <button onClick={() => openEdit(b)} style={actionBtn('#8E8E93')}>✏️ Изменить</button>
              <button
                onClick={() => handleSendTelegram(b.id)}
                disabled={sendingTg === b.id}
                style={actionBtn('#229ED9')}
              >
                {sendingTg === b.id ? '⏳' : '✈️ Telegram'}
              </button>
              {userRole !== 'waiter' && (
                <button onClick={() => handleDelete(b.id)} style={actionBtn('#E85D4A', true)}>🗑️</button>
              )}
            </div>
          </div>
        );
      })}

      {/* ── Форма ──────────────────────────────────────────────── */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }} onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '20px 20px 0 0',
            width: '100%', maxWidth: '600px', maxHeight: '92vh',
            display: 'flex', flexDirection: 'column',
            padding: '20px', boxSizing: 'border-box',
          }}>
            {/* Заголовок */}
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: '16px', flexShrink: 0 }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>
                {editId ? '✏️ Редактировать банкет' : '🥂 Новый банкет'}
              </h2>
              <button onClick={() => setShowForm(false)} style={{
                width: '32px', height: '32px', borderRadius: '50%', border: 'none',
                backgroundColor: '#F0F0F0', fontSize: '18px', cursor: 'pointer',
              }}>×</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {/* Основная инфо */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                <Field label="Имя клиента *">
                  <input value={form.customer_name}
                    onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))}
                    placeholder="Иванов Иван" style={inputStyle} />
                </Field>
                <Field label="Телефон (необязательно)">
                  <UzPhoneField
                    value={form.phone}
                    onChange={(v) => {
                      setForm((f) => ({ ...f, phone: v }));
                      setPhoneError('');
                    }}
                    error={phoneError}
                  />
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <Field label="Дата *">
                    <input value={form.date}
                      onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
                      type="date" style={inputStyle} />
                  </Field>
                  <Field label="Время *">
                    <input value={form.time}
                      onChange={(e) => setForm(f => ({ ...f, time: e.target.value }))}
                      type="time" style={inputStyle} />
                  </Field>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <Field label="Кол-во гостей">
                    <input value={form.guests_count}
                      onChange={(e) => setForm(f => ({ ...f, guests_count: e.target.value }))}
                      type="number" min="1" placeholder="10" style={inputStyle} />
                  </Field>
                  <Field label="Предоплата (сум)">
                    <input value={form.prepayment}
                      onChange={(e) => setForm(f => ({ ...f, prepayment: e.target.value }))}
                      type="number" min="0" placeholder="0" style={inputStyle} />
                  </Field>
                </div>
                <Field label="Заметки / пожелания">
                  <textarea value={form.notes}
                    onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Особые пожелания, украшение зала..." rows={2}
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
                </Field>
              </div>

              {/* Выбор меню */}
              {menuItems.length > 0 && (
                <div style={{
                  border: '1px solid #F0F0F0', borderRadius: '14px',
                  padding: '12px', marginBottom: '14px',
                }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#555', marginBottom: '10px' }}>
                    🍽️ Меню для банкета
                  </div>

                  {/* Категории */}
                  {categories.length > 0 && (
                    <div style={{ display: 'flex', gap: '6px', overflowX: 'auto',
                      paddingBottom: '6px', marginBottom: '8px', scrollbarWidth: 'none' }}>
                      <CatBtn active={catFilter === 'all'} onClick={() => setCatFilter('all')}>Все</CatBtn>
                      {categories.map((c) => (
                        <CatBtn key={c.id} active={catFilter === c.id} onClick={() => setCatFilter(c.id)}>
                          {c.name_ru || c.name}
                        </CatBtn>
                      ))}
                    </div>
                  )}

                  <input placeholder="🔍 Поиск..." value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ ...inputStyle, marginBottom: '8px' }} />

                  <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                    {filtered.map((item) => (
                      <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F5F5F5' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: '500' }}>{item.name_ru || item.name}</div>
                          <div style={{ fontSize: '11px', color: '#999' }}>{fmt(item.price)} сум</div>
                        </div>
                        {cart[item.id] ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <QtyBtn bg="#F0F0F0" color="#333" onClick={() => changeQty(item.id, -1)}>−</QtyBtn>
                            <span style={{ fontSize: '13px', fontWeight: '700', minWidth: '18px', textAlign: 'center' }}>
                              {cart[item.id]}
                            </span>
                            <QtyBtn bg="#000" color="#fff" onClick={() => changeQty(item.id, +1)}>+</QtyBtn>
                          </div>
                        ) : (
                          <button onClick={() => addItem(item)} style={{
                            padding: '5px 10px', borderRadius: '8px', border: 'none',
                            backgroundColor: '#000', color: '#fff',
                            fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                          }}>+ Добавить</button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Корзина + расчёт */}
              {cartItems.length > 0 && (
                <div style={{
                  backgroundColor: '#F8F8F8', borderRadius: '14px',
                  padding: '12px', marginBottom: '14px',
                }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#555', marginBottom: '8px' }}>
                    🛒 Состав меню:
                  </div>
                  {cartItems.map((item) => (
                    <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #EFEFEF' }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: '13px' }}>{item.name_ru || item.name}</span>
                        <span style={{ fontSize: '12px', color: '#999', marginLeft: '6px' }}>× {cart[item.id]}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#555' }}>
                          {fmt(Number(item.price) * cart[item.id])} сум
                        </span>
                        <QtyBtn bg="#FFEBEE" color="#E85D4A" onClick={() => changeQty(item.id, -1)}>−</QtyBtn>
                        <QtyBtn bg="#E8F5E9" color="#34C759" onClick={() => changeQty(item.id, +1)}>+</QtyBtn>
                      </div>
                    </div>
                  ))}

                  {/* Скидка */}
                  <div style={{ marginTop: '10px', marginBottom: '8px' }}>
                    <Field label="🏷️ Скидка на меню (%)">
                      <input value={form.discount}
                        onChange={(e) => setForm(f => ({ ...f, discount: e.target.value }))}
                        type="number" min="0" max="100" placeholder="0" style={inputStyle} />
                    </Field>
                  </div>

                  {/* Итог */}
                  <div style={{ fontSize: '13px', paddingTop: '4px' }}>
                    {discountPct > 0 && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#999', marginBottom: '2px' }}>
                          <span>Подитог</span><span>{fmt(subtotal)} сум</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#FF9500', marginBottom: '2px' }}>
                          <span>Скидка {discountPct}%</span><span>−{fmt(discountAmt)} сум</span>
                        </div>
                      </>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      fontWeight: '700', fontSize: '15px', marginBottom: '2px' }}>
                      <span>Итого</span><span>{fmt(total)} сум</span>
                    </div>
                    {prepayment > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#E85D4A' }}>
                        <span>Остаток</span><span>{fmt(balance)} сум</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Кнопки */}
            <div style={{ display: 'flex', gap: '10px', paddingTop: '12px',
              borderTop: '1px solid #F0F0F0', flexShrink: 0,
              paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
              <button onClick={() => setShowForm(false)} style={{
                flex: 1, padding: '14px', borderRadius: '12px', border: 'none',
                backgroundColor: '#F0F0F0', color: '#333',
                fontSize: '15px', fontWeight: '600', cursor: 'pointer',
              }}>Отмена</button>
              <button onClick={handleSave} disabled={saving} style={{
                flex: 2, padding: '14px', borderRadius: '12px', border: 'none',
                backgroundColor: saving ? '#ccc' : '#007AFF', color: '#fff',
                fontSize: '15px', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer',
              }}>
                {saving ? '⏳ Сохраняем...'
                  : cartItems.length > 0
                    ? `${editId ? '💾 Сохранить' : '+ Создать'} · ${fmt(total)} сум`
                    : (editId ? '💾 Сохранить' : '+ Создать банкет')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Вспомогательные компоненты ────────────────────────────────
function InfoCell({ icon, label, value, valueColor }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontSize: '11px', color: '#999', fontWeight: '500' }}>{icon} {label}</span>
      <span style={{ fontSize: '13px', color: valueColor || '#333', fontWeight: '600' }}>{value || '—'}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '5px' }}>{label}</div>
      {children}
    </div>
  );
}

function CatBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      flexShrink: 0, padding: '5px 12px', borderRadius: '100px', border: 'none',
      backgroundColor: active ? '#000' : '#F0F0F0',
      color: active ? '#fff' : '#555',
      fontSize: '12px', fontWeight: '600', cursor: 'pointer',
    }}>{children}</button>
  );
}

function QtyBtn({ bg, color, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      width: '26px', height: '26px', borderRadius: '7px', border: 'none',
      backgroundColor: bg, color, fontSize: '15px', fontWeight: '700',
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{children}</button>
  );
}

const inputStyle = {
  width: '100%', padding: '11px 12px', borderRadius: '10px',
  border: '1px solid #E0E0E0', fontSize: '14px',
  outline: 'none', boxSizing: 'border-box',
};

const primaryBtn = {
  padding: '8px 14px', borderRadius: '10px', border: 'none',
  backgroundColor: '#000', color: '#fff',
  fontSize: '13px', fontWeight: '700', cursor: 'pointer',
};

const ghostBtn = {
  padding: '8px 10px', borderRadius: '10px', border: 'none',
  backgroundColor: '#F0F0F0', color: '#333',
  fontSize: '14px', cursor: 'pointer',
};

const actionBtn = (color, outline = false) => ({
  padding: '7px 12px', borderRadius: '10px',
  border: outline ? `1px solid ${color}` : 'none',
  backgroundColor: outline ? 'transparent' : color + '22',
  color, fontSize: '12px', fontWeight: '600', cursor: 'pointer',
});
